import React from "react";
import { Crown } from "lucide-react";
import "./sidebarUpgradePanel.css";

interface SidebarUpgradePanelProps {
  onUpgrade: () => void | Promise<boolean>;
  disabled?: boolean;
  loading?: boolean;
  mode?: "upgrade" | "beta";
  betaLabel?: string | null;
  userName?: string;
  userEmail?: string;
  userAvatar?: string;
  productName?: string;
  showUserInfo?: boolean;
  showUpgradeCard?: boolean;
}

const getFallbackName = (userName?: string, userEmail?: string) => {
  if (userName?.trim()) {
    return userName.trim();
  }

  if (!userEmail) {
    return "Signed in";
  }

  const localPart = userEmail.split("@")[0] || "User";
  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(" ");
};

const SidebarUpgradePanel: React.FC<SidebarUpgradePanelProps> = ({
  onUpgrade,
  disabled = false,
  loading = false,
  mode = "upgrade",
  betaLabel,
  userName,
  userEmail,
  userAvatar,
  productName = "Impret",
  showUserInfo = true,
  showUpgradeCard = true,
}) => {
  const displayName = getFallbackName(userName, userEmail);
  const avatarLabel = (displayName || userEmail || "U").charAt(0).toUpperCase();
  const isBetaMode = mode === "beta";

  return (
    <section
      className={`sidebar-upgrade-panel${showUpgradeCard ? "" : " sidebar-upgrade-panel--identity-only"}`}
    >
      {showUpgradeCard && (
        <div className="sidebar-upgrade-panel__card">
          <div className="sidebar-upgrade-panel__glow" aria-hidden="true" />
          <div className="sidebar-upgrade-panel__title-row">
            <div className="sidebar-upgrade-panel__title-wrap">
              <h3 className="sidebar-upgrade-panel__title">{productName}</h3>
              <span className="sidebar-upgrade-panel__badge">
                <Crown size={11} />
                {isBetaMode ? (betaLabel || "BETA").toUpperCase() : "UPGRADE"}
              </span>
            </div>
          </div>

          <p className="sidebar-upgrade-panel__subtitle">
            {isBetaMode
              ? "All features are unlocked during the beta. Explore the full workflow while checkout stays off."
              : "Upgrade for more brands, more generations, and agency capacity."}
          </p>

          <button
            type="button"
            className="sidebar-upgrade-panel__button"
            onClick={() => {
              void onUpgrade();
            }}
            disabled={isBetaMode || disabled || loading}
          >
            {isBetaMode ? "Beta is live" : loading ? "Opening..." : "Upgrade"}
          </button>
        </div>
      )}

      {showUserInfo && (
        <div className="sidebar-upgrade-panel__user">
          <div className="sidebar-upgrade-panel__avatar-wrap">
            {userAvatar ? (
              <img
                src={userAvatar}
                alt={displayName}
                className="sidebar-upgrade-panel__avatar-image"
              />
            ) : (
              <span className="sidebar-upgrade-panel__avatar-fallback">
                {avatarLabel}
              </span>
            )}
          </div>

          <div className="sidebar-upgrade-panel__identity">
            <div className="sidebar-upgrade-panel__name">{displayName}</div>
            <div className="sidebar-upgrade-panel__email">
              {userEmail || "Signed in"}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SidebarUpgradePanel;
